import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { ArrowLeft, ChevronDown, ChevronRight, Eye, Search } from 'react-bootstrap-icons';
import { useSearchParams } from 'react-router-dom';
import { clientService, groupClientsByType } from '@/services/clientService';
import { creditsService, DEFAULT_CREDIT_CONFIG, type CreditStanding } from '@/services/creditsService';
import BillingAdminsPanel from '@/components/BillingAdminsPanel';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import FleetCreditsTable from '@/components/FleetCreditsTable';
import { CopyButton } from '@/components/ui/CopyButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { resolveContract } from '@/config/contract';
import { useClientFlags } from '@/hooks/useClientFlags';
import { DEFAULT_FX_NZD, fetchFxNzd, getFxNzdCached } from '@/services/fxService';
import { notBilledText } from '@/utils/contractText';
import { formatNzDate, formatNzDateTime } from '@/utils/relativeTime';
import type { Client, CreditConfig } from '@/types';
import './NumaCredits.css';

/**
 * Credits: where Arcanum sets what each client is charged and how many credits it gets.
 *
 * The list on the left picks a client. With none picked the page shows every client in one
 * table; with one picked it shows that client's month, its monthly credits, its top-up
 * balance, who can see billing, and its pricing. A save writes the plan to the central
 * client config and to the client's own ledger. The client can read its credits, never set them.
 *
 * Only a client on Credits, or a partner billed as Credits, and not inactive, is billed for
 * credits used past its monthly credits (`isCollectible`). Any other client shows the same
 * use and history, and the page never calls it owed. The list on the left leaves inactive
 * clients out until "Show inactive" is on.
 */

const TIERS = ['low', 'medium', 'high', 'very_high'] as const;
const CONTEXTS = ['chat', 'agent'] as const;
const TIER_LABEL: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High', very_high: 'Very high' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// synergyRates is tolerated on old records but retired from authoring (Synergy
// sync bills at measured cost since TASK-380), so it is excluded from the full shape.
type FullConfig = Omit<Required<CreditConfig>, 'synergyRates'>;

function withDefaults(c?: CreditConfig): FullConfig {
  return {
    ...DEFAULT_CREDIT_CONFIG,
    ...c,
    valueTiers: {
      chat: { ...DEFAULT_CREDIT_CONFIG.valueTiers.chat, ...c?.valueTiers?.chat },
      agent: { ...DEFAULT_CREDIT_CONFIG.valueTiers.agent, ...c?.valueTiers?.agent },
    },
    marginsByTier: { ...DEFAULT_CREDIT_CONFIG.marginsByTier, ...c?.marginsByTier },
    voiceRates: { ...DEFAULT_CREDIT_CONFIG.voiceRates, ...c?.voiceRates },
    monthlyAllocations:
      c?.monthlyAllocations && c.monthlyAllocations.length === 12
        ? c.monthlyAllocations
        : DEFAULT_CREDIT_CONFIG.monthlyAllocations,
  };
}

/** Whether the picked client's ledger has been read, is being read, or could not be read. */
type StandingState = 'loading' | 'ready' | 'failed';

/** A write that waits for the person to confirm it. */
type Pending = { kind: 'topup'; credits: number } | { kind: 'reset' } | null;

/** One figure in the "this month" strip. The label carries the unit. */
function Stat({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div className="credits-stat">
      <div className="credits-stat__label">{label}</div>
      <div className={`credits-stat__value numeric${danger ? ' credits-stat__value--danger' : ''}`}>{value}</div>
      {sub && <div className={`credits-stat__sub${danger ? ' credits-stat__sub--danger' : ''}`}>{sub}</div>}
    </div>
  );
}

export default function NumaCredits() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The picked client lives in the address, so a client's credits can be linked to.
  const selected = searchParams.get('client') ?? '';
  const selectClient = useCallback((name: string) => setSearchParams(name ? { client: name } : {}), [setSearchParams]);

  const [clients, setClients] = useState<Client[]>([]);
  const [railSearch, setRailSearch] = useState('');
  const [railShowInactive, setRailShowInactive] = useState(false);
  // Each client's contract and tags, from the shared reader.
  const flags = useClientFlags();
  const [config, setConfig] = useState<FullConfig>(withDefaults());
  const [savedConfig, setSavedConfig] = useState<FullConfig>(withDefaults());
  const [topUp, setTopUp] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [standing, setStanding] = useState<CreditStanding | null>(null);
  const [standingState, setStandingState] = useState<StandingState>('loading');
  const [pricingOpen, setPricingOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  // NZD per USD: a display-only sense-check (billing stays USD). Seeds from the session cache and
  // refreshes to the live ECB reference rate on mount; the operator can still type their own.
  const [fxNzd, setFxNzd] = useState(() => getFxNzdCached().rate);
  const [showCredits, setShowCredits] = useState(false); // in-app credits view visible to the client?

  const loadClients = useCallback(async () => {
    try {
      setClients(await clientService.getAllClients());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The client list did not load. Reload the page to try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  useEffect(() => {
    let cancelled = false;
    void fetchFxNzd().then((fx) => {
      if (!cancelled && fx.live) setFxNzd(fx.rate);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const client = useMemo(() => clients.find((c) => c.name === selected), [clients, selected]);

  useEffect(() => {
    setNotice(null);
    setError(null);
    const c = withDefaults(client?.config.creditConfig);
    setConfig(c);
    setSavedConfig(c); // dirty-tracking baseline
    setShowCredits(!!client?.config?.showCredits);
    // The last client's balance must never show, or be acted on, under this client's name.
    setStanding(null);
    setStandingState('loading');
    setPending(null);
    setTopUp('');
  }, [client]);

  // A long client list leaves the page scrolled far down; a newly opened client starts at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [selected]);

  // Counts ledger reads, so an answer that comes back after the person moved on is dropped.
  const standingRead = useRef(0);
  const refreshStanding = useCallback(async () => {
    const read = ++standingRead.current;
    if (!client) {
      setStanding(null);
      return;
    }
    try {
      const next = await creditsService.getStanding(client.name);
      if (read !== standingRead.current) return;
      setStanding(next);
      setStandingState('ready');
    } catch {
      if (read !== standingRead.current) return;
      setStanding(null); // standing is informational: never block authoring on a read failure
      setStandingState('failed');
    }
  }, [client]);

  useEffect(() => {
    void refreshStanding();
  }, [refreshStanding]);

  const isCustom = !!client?.config.creditConfig;
  const clientRow = client ? flags.row(client.name) : undefined;
  // A client on its own AWS account pays AWS directly: it has no credits, so no plan to set.
  const ownAccount =
    client?.config.contractType === 'own-account' || resolveContract(clientRow, client?.config) === 'own-account';
  // Why this client is not billed for use past its monthly credits. Null when it is billed.
  const notBilled = client ? notBilledText(clientRow, client.config, flags.isInactive(client.name)) : null;
  const dirty = useMemo(() => JSON.stringify(config) !== JSON.stringify(savedConfig), [config, savedConfig]);

  // ── NZ billing calendar (current month) ──
  const nzNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Pacific/Auckland' }));
  const year = nzNow.getFullYear();
  const curIdx = nzNow.getMonth(); // 0=Jan, on the NZ calendar
  const monthKey = (i: number): string => `${year}-${String(i + 1).padStart(2, '0')}`;

  const num = (v: string): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const setGlobal = (k: keyof FullConfig, v: number) => setConfig((c) => ({ ...c, [k]: v }));
  const setTier = (ctx: string, tier: string, v: number) =>
    setConfig((c) => ({ ...c, valueTiers: { ...c.valueTiers, [ctx]: { ...c.valueTiers[ctx], [tier]: v } } }));
  const setMargin = (tier: string, v: number) =>
    setConfig((c) => ({ ...c, marginsByTier: { ...c.marginsByTier, [tier]: v } }));
  const setVoiceRate = (k: 'telephonyPerMin' | 'transcribePerMin' | 'contactLensPerMin', v: number) =>
    setConfig((c) => ({ ...c, voiceRates: { ...c.voiceRates, [k]: v } }));
  const setAllMonths = (v: number) =>
    setConfig((c) => ({ ...c, monthlyAllocations: Array.from({ length: 12 }, () => v) }));
  const setMonth = (i: number, v: number) =>
    setConfig((c) => {
      const next = Array.from({ length: 12 }, (_, j) => c.monthlyAllocations[j] ?? 0);
      next[i] = v;
      return { ...c, monthlyAllocations: next };
    });

  const handleSave = async () => {
    if (!client) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await creditsService.saveConfig(client.name, config);
      setSavedConfig(config); // clears the dirty bar
      setNotice(`Saved the monthly credits and pricing for ${client.name}, and sent them to the client.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The save did not finish. Check the client before you try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setConfig(savedConfig);
    setNotice(null);
    setError(null);
  };

  /** Check the amount, then ask the person to confirm it. Nothing is written here. */
  const askTopUp = () => {
    if (!client) return;
    const credits = num(topUp);
    if (credits <= 0) {
      setError('Enter a number of credits above zero.');
      return;
    }
    setError(null);
    setPending({ kind: 'topup', credits });
  };

  const handleTopUp = async (credits: number) => {
    if (!client) return;
    setPending(null);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await creditsService.topUp(client.name, credits);
      setNotice(`Added ${credits.toLocaleString()} credits to ${client.name}.`);
      setTopUp('');
      void refreshStanding();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The top-up failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!client) return;
    setPending(null);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const defaults = withDefaults(); // pure code defaults (pricing + 500/mo allocation)
      const { name } = client;
      await creditsService.saveConfig(name, defaults);
      const settled = standing?.settledBalance ?? 0;
      if (settled !== 0) {
        await creditsService.adjustBalance(name, -settled, 'reset to defaults');
      }
      setConfig(defaults);
      setSavedConfig(defaults);
      setNotice(
        settled !== 0
          ? `Reset ${name} to the default plan and recorded an adjustment of ${(-settled).toLocaleString()} credits on its balance.`
          : `Reset ${name} to the default plan. Its top-up balance was not changed.`
      );
      void refreshStanding();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The reset failed part way. Check the client before you try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (next: boolean) => {
    if (!client) return;
    setShowCredits(next); // optimistic
    setError(null);
    setNotice(null);
    try {
      await creditsService.setVisibility(client.name, next);
      setNotice(
        next
          ? `${client.name} sees credits in its app after its next deploy.`
          : `Credits are hidden from ${client.name} after its next deploy.`
      );
    } catch (e) {
      setShowCredits(!next); // revert on failure
      setError(e instanceof Error ? e.message : 'The change did not save.');
    }
  };

  // ── per-client derivations ──
  const usedCredits = (i: number): number => standing?.months[monthKey(i)]?.used ?? 0;
  // Internal economics for the month (Arcanum-only): real consumption cost (USD) + realised margin.
  const monthCostUsd = (i: number): number => standing?.months[monthKey(i)]?.costUsd ?? 0;
  const monthMargin = (i: number): number | null => {
    const m = standing?.months[monthKey(i)];
    return m && m.costUsd > 0 ? m.revenueUsd / m.costUsd : null;
  };
  const nzd = (credits: number): number => credits * config.creditUsd * fxNzd;
  const nzdLabel = (credits: number): string =>
    `about NZD $${nzd(credits).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const curAllocated = config.monthlyAllocations[curIdx] ?? 0;
  const curUsed = usedCredits(curIdx);
  const curRemaining = curAllocated - curUsed;
  const usedPct = curAllocated > 0 ? Math.min(100, Math.round((curUsed / curAllocated) * 100)) : 0;
  const annualAllocation = config.monthlyAllocations.reduce((s, v) => s + (v ?? 0), 0);
  const balance = standing?.availableBalance ?? 0;
  const standingReady = standingState === 'ready';
  // A figure that comes from the ledger is blank until the ledger has been read.
  const fromLedger = (n: number): string => (standingReady ? n.toLocaleString() : '');

  // ── rail grouping ──
  const { devClients, productionClients } = useMemo(() => groupClientsByType(clients), [clients]);
  const matches = (c: Client) => c.name.toLowerCase().includes(railSearch.trim().toLowerCase());
  // An inactive client is left out until "Show inactive" is on. The open client always stays in the list.
  const railListed = (c: Client) => railShowInactive || !flags.isInactive(c.name) || c.name === selected;
  const railDev = devClients.filter(railListed).filter(matches);
  const railProd = productionClients.filter(railListed).filter(matches);
  const railInactiveHidden = railShowInactive
    ? 0
    : clients.filter((c) => flags.isInactive(c.name) && c.name !== selected).length;
  const railGroups = [
    { label: 'Customers', clients: railProd },
    { label: 'Dev and demo', clients: railDev },
  ];

  // Nearly every client has a plan of its own, so the list marks the few that do not.
  const railItem = (c: Client) => (
    <li key={c.name}>
      <button
        type="button"
        className={`credits-rail__item${selected === c.name ? ' is-active' : ''}`}
        aria-current={selected === c.name ? 'true' : undefined}
        onClick={() => selectClient(c.name)}
      >
        <span className="credits-rail__name">{c.name}</span>
        {flags.isInactive(c.name) && <span className="credits-rail__note">Inactive</span>}
        {!c.config.creditConfig && <span className="credits-rail__note">Default plan</span>}
        {c.config.showCredits && <Eye className="credits-rail__eye" title="The client sees credits in its app" />}
      </button>
    </li>
  );

  const defaultMonthly = DEFAULT_CREDIT_CONFIG.monthlyAllocations[0] ?? 0;
  const nzdAt = (credits: number) =>
    `about NZD $${(credits * DEFAULT_CREDIT_CONFIG.creditUsd * fxNzd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  // What a reset does to the balance, said before the person confirms it. The reset cancels the
  // top-ups and settlements recorded so far; usage not yet settled is outside it.
  const settledNow = standing?.settledBalance ?? 0;
  const overflowNow = standing?.liveOverflow ?? 0;
  const resetBalanceLine =
    standingState === 'loading'
      ? 'Its top-up balance is still being read.'
      : standingState === 'failed'
        ? 'Its top-up balance could not be read, so the balance stays as it is.'
        : settledNow !== 0
          ? `An adjustment of ${(-settledNow).toLocaleString()} credits is recorded in its ledger. That cancels every top-up and settlement so far. Nothing is deleted.`
          : 'Its ledger holds no top-ups or settlements to cancel, so the balance stays as it is.';

  return (
    <div className={`credits-page${client && dirty ? ' credits-page--dirty' : ''}`}>
      <PageHeader title="Credits" />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <div className="credits-layout">
        {/* ── The client list ── */}
        <nav className="credits-rail" aria-label="Clients">
          <InputGroup size="sm" className="credits-rail__search">
            <InputGroup.Text>
              <Search />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search clients"
              value={railSearch}
              onChange={(e) => setRailSearch(e.target.value)}
              aria-label="Search clients"
            />
          </InputGroup>
          <button
            type="button"
            className={`credits-rail__item credits-rail__item--all${selected ? '' : ' is-active'}`}
            aria-current={selected ? undefined : 'true'}
            onClick={() => selectClient('')}
          >
            All clients
          </button>
          {loading && (
            <div className="credits-rail__state">
              <Spinner animation="border" size="sm" /> Loading clients
            </div>
          )}
          {railGroups.map(
            (group) =>
              group.clients.length > 0 && (
                <div key={group.label} className="credits-rail__group">
                  <h2 className="section-label credits-rail__label">{group.label}</h2>
                  <ul className="credits-rail__list">{group.clients.map(railItem)}</ul>
                </div>
              )
          )}
          {!loading && clients.length > 0 && railProd.length + railDev.length === 0 && (
            <p className="credits-rail__state">No client matches that search.</p>
          )}
          {!loading && clients.length > 0 && (
            <Form.Check
              type="switch"
              id="credits-rail-inactive"
              className="credits-rail__inactive"
              label={
                railShowInactive
                  ? 'Show inactive'
                  : `Show inactive${railInactiveHidden > 0 ? ` (${railInactiveHidden.toLocaleString()})` : ''}`
              }
              checked={railShowInactive}
              onChange={(e) => setRailShowInactive(e.target.checked)}
            />
          )}
          {!loading && clients.length > 0 && (
            <p className="credits-rail__legend">
              <Eye aria-hidden="true" /> The client sees credits in its app.
            </p>
          )}
        </nav>

        {/* ── The main pane ── */}
        <div className="credits-main">
          {selected && !client && loading && (
            <div className="credits-empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading {selected}
            </div>
          )}

          {selected && !client && !loading && (
            <Alert variant="warning">
              There is no client named <span className="mono">{selected}</span>. Pick one from the list.
            </Alert>
          )}

          {!client && !(selected && loading) && (
            /* ───────── Every client: the table, with the default plan above it ───────── */
            <>
              <Card className="credits-defaults">
                <button
                  type="button"
                  className="credits-defaults__toggle"
                  aria-expanded={defaultsOpen}
                  onClick={() => setDefaultsOpen((o) => !o)}
                >
                  <span className="credits-defaults__title">Default plan</span>
                  <span className="credits-defaults__summary">
                    {defaultMonthly.toLocaleString()} credits a month at ${DEFAULT_CREDIT_CONFIG.creditUsd} USD a credit
                    (about NZD ${(DEFAULT_CREDIT_CONFIG.creditUsd * fxNzd).toFixed(2)}). A client without a plan of its
                    own gets this.
                  </span>
                  <span className="credits-defaults__more">
                    {defaultsOpen ? 'Hide pricing' : 'Show pricing'}
                    {defaultsOpen ? <ChevronDown /> : <ChevronRight />}
                  </span>
                </button>
                <Collapse in={defaultsOpen}>
                  <div>
                    <div className="credits-defaults__body">
                      <dl className="credits-facts">
                        <div>
                          <dt>One credit</dt>
                          <dd>
                            ${DEFAULT_CREDIT_CONFIG.creditUsd} USD
                            <span className="credits-facts__sub">
                              about NZD ${(DEFAULT_CREDIT_CONFIG.creditUsd * fxNzd).toFixed(2)}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt>Monthly credits</dt>
                          <dd>
                            {defaultMonthly.toLocaleString()}
                            <span className="credits-facts__sub">{nzdAt(defaultMonthly)} a month</span>
                          </dd>
                        </div>
                        <div>
                          <dt>Billing</dt>
                          <dd>
                            In USD
                            <span className="credits-facts__sub">by New Zealand calendar month</span>
                          </dd>
                        </div>
                        <div>
                          <dt>AgentCore multiplier</dt>
                          <dd>
                            {DEFAULT_CREDIT_CONFIG.agentcoreMult}×
                            <span className="credits-facts__sub">added to the token cost</span>
                          </dd>
                        </div>
                      </dl>

                      <Table size="sm" className="credits-tiers mb-0">
                        <thead>
                          <tr>
                            <th>For each tier</th>
                            {TIERS.map((t) => (
                              <th key={t} className="text-end">
                                {TIER_LABEL[t]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Credits for a chat</td>
                            {TIERS.map((t) => (
                              <td key={t} className="text-end numeric">
                                {DEFAULT_CREDIT_CONFIG.valueTiers.chat[t]}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td>Credits for an agent run</td>
                            {TIERS.map((t) => (
                              <td key={t} className="text-end numeric">
                                {DEFAULT_CREDIT_CONFIG.valueTiers.agent[t]}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td>Lowest margin allowed</td>
                            {TIERS.map((t) => (
                              <td key={t} className="text-end numeric">
                                {DEFAULT_CREDIT_CONFIG.marginsByTier[t]}×
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </Table>

                      <p className="credits-hint mb-0">
                        A conversation is charged the larger of two numbers: the credits for its tier, or its token cost
                        × the AgentCore multiplier × the tier&apos;s margin ÷ the price of a credit, rounded up. A plain
                        chat uses the chat row. Agent chats and scheduled runs use the agent row. Open a client to give
                        it a plan of its own.
                      </p>
                    </div>
                  </div>
                </Collapse>
              </Card>

              <FleetCreditsTable
                clients={clients}
                loading={loading}
                onOpenClient={selectClient}
                onDataChanged={() => void loadClients()}
              />
            </>
          )}

          {client && (
            /* ───────── One client ───────── */
            <>
              <button type="button" className="credits-back" onClick={() => selectClient('')}>
                <ArrowLeft aria-hidden="true" /> All clients
              </button>

              <div className="credits-client-head">
                <div className="credits-client-head__main">
                  <h2 className="credits-client-head__name">
                    {client.name}
                    <ClientTags clientName={client.name} flags={flags} config={client.config} hide="deploy-block" />
                  </h2>
                  <div className="credits-client-head__facts">
                    <span>
                      Account <span className="mono">{client.config.clientAccountId}</span>
                      <CopyButton value={client.config.clientAccountId} label="Copy account id" />
                    </span>
                    <span className="mono">{client.config.region}</span>
                    <span>
                      {ownAccount ? 'Own account' : isCustom ? 'Has a plan of its own' : 'On the default plan'}
                    </span>
                  </div>
                </div>
                <div className="credits-client-head__switch">
                  <Form.Check
                    type="switch"
                    id="show-credits-switch"
                    label={ownAccount ? "Show costs in the client's app" : "Show credits in the client's app"}
                    checked={showCredits}
                    disabled={saving}
                    onChange={(e) => handleToggleVisibility(e.target.checked)}
                  />
                  <div className="credits-hint">
                    Takes effect on the client&apos;s next deploy. Usage is metered either way.
                  </div>
                </div>
              </div>

              {ownAccount && (
                <Card className="credits-card">
                  <Card.Header>No credit plan</Card.Header>
                  <Card.Body>
                    <p className="mb-2">
                      Numa runs in {client.name}&apos;s own AWS account, and {client.name} pays AWS directly. It has no
                      credits, monthly credits or top-ups.
                    </p>
                    <p className="credits-hint mb-0">
                      Its people see each cost as the real AWS dollars. No credit limit stops their work, and the
                      overage alerts leave it out. To change this, set Contract Type in the client&apos;s config.
                    </p>
                  </Card.Body>
                </Card>
              )}

              {!ownAccount && notBilled && (
                <Alert variant="secondary" className="credits-not-billed">
                  <strong>{notBilled}.</strong> Its use past the monthly credits shows here as past allocation, and
                  nobody is invoiced for it.
                </Alert>
              )}

              {/* ── This month ── */}
              {!ownAccount && (
                <Card className="credits-standing">
                  <div className="credits-standing__stats">
                    <Stat label={`Credits for ${MONTHS[curIdx]} ${year}`} value={curAllocated.toLocaleString()} />
                    <Stat label="Credits used this month" value={fromLedger(curUsed)} />
                    <Stat
                      label="Credits left this month"
                      value={fromLedger(curRemaining)}
                      danger={standingReady && curRemaining < 0}
                      sub={
                        standingReady && curRemaining < 0
                          ? notBilled
                            ? `Over by ${Math.abs(curRemaining).toLocaleString()}. Not billed.`
                            : `Over by ${Math.abs(curRemaining).toLocaleString()}. The top-up balance pays for it.`
                          : undefined
                      }
                    />
                    <Stat
                      label="Top-up balance, credits"
                      value={fromLedger(balance)}
                      danger={standingReady && balance < 0 && !notBilled}
                      sub={
                        !standingReady
                          ? undefined
                          : balance < 0
                            ? notBilled
                              ? `Past allocation: ${Math.abs(balance).toLocaleString()} credits. Not billed.`
                              : `Owed: ${Math.abs(balance).toLocaleString()} credits, ${nzdLabel(Math.abs(balance))}`
                            : nzdLabel(balance)
                      }
                    />
                  </div>
                  {standingState === 'loading' && (
                    <div className="credits-standing__state">
                      <Spinner animation="border" size="sm" /> Reading this client&apos;s usage and balance
                    </div>
                  )}
                  {standingState === 'failed' && (
                    <div className="credits-standing__state credits-standing__state--failed">
                      This client&apos;s usage and balance could not be read. Its ledger may not exist yet. You can
                      still change its monthly credits and pricing.
                      <Button size="sm" variant="outline-secondary" onClick={() => void refreshStanding()}>
                        Try again
                      </Button>
                    </div>
                  )}
                  {standingReady && curAllocated > 0 && (
                    <div className="credits-standing__bar">
                      <ProgressBar now={usedPct} variant={curUsed > curAllocated ? 'danger' : undefined} />
                      <span className="numeric">
                        {curUsed > curAllocated ? "Over this month's credits" : `${usedPct}% used`}
                      </span>
                    </div>
                  )}
                  <p className="credits-standing__note">
                    Each New Zealand month starts with the monthly credits, and unused ones do not carry over. Usage
                    draws on them first. Usage past them draws on the top-up balance, which carries over and can go
                    below zero.{' '}
                    {notBilled
                      ? `${notBilled}, so a balance below zero is use past allocation and nobody is invoiced for it.`
                      : 'A balance below zero is what the client is invoiced for.'}{' '}
                    Nothing is cut off at zero.
                  </p>
                  <div className="credits-standing__foot">
                    <span>
                      Monthly credits for {year}:{' '}
                      <strong className="numeric">{annualAllocation.toLocaleString()}</strong> credits,{' '}
                      {nzdLabel(annualAllocation)} a year.
                    </span>
                    <label className="credits-fx">
                      NZD amounts use
                      <Form.Control
                        type="number"
                        step="0.01"
                        size="sm"
                        className="credits-fx__input"
                        value={fxNzd}
                        onChange={(e) => setFxNzd(num(e.target.value) || DEFAULT_FX_NZD)}
                      />
                      NZD to the USD. They are a guide only: billing is in USD.
                    </label>
                  </div>
                </Card>
              )}

              {!ownAccount && (
                <Row className="g-3">
                  <Col lg={7}>
                    {/* ── Monthly credits ── */}
                    <Card className="credits-card">
                      <Card.Header>Monthly credits</Card.Header>
                      <Card.Body>
                        <div className="credits-all-months">
                          <div>
                            <Form.Label htmlFor="credits-all-months">Set every month to</Form.Label>
                            <Form.Control
                              id="credits-all-months"
                              type="number"
                              step="100"
                              placeholder="5000"
                              onChange={(e) => {
                                if (e.target.value !== '') setAllMonths(num(e.target.value));
                              }}
                            />
                          </div>
                          <p className="credits-hint mb-0">
                            Type one number for a flat plan, or set each month below. Under each month is what the
                            client used in {year}, with what that usage cost Arcanum.
                          </p>
                        </div>
                        <div className="credits-months">
                          {MONTHS.map((m, i) => (
                            <div key={m} className={`credits-month${i === curIdx ? ' credits-month--now' : ''}`}>
                              <Form.Label htmlFor={`credits-month-${i}`}>
                                {m}
                                {i === curIdx && <span className="credits-month__now">This month</span>}
                              </Form.Label>
                              <Form.Control
                                id={`credits-month-${i}`}
                                type="number"
                                step="100"
                                value={config.monthlyAllocations[i] ?? 0}
                                onChange={(e) => setMonth(i, num(e.target.value))}
                              />
                              <div className="credits-month__used numeric">
                                {standingReady ? `${usedCredits(i).toLocaleString()} used` : ''}
                                {monthCostUsd(i) > 0 && (
                                  <span className="credits-month__cost">
                                    ${monthCostUsd(i).toFixed(2)} cost
                                    {monthMargin(i) != null && `, ${monthMargin(i)!.toFixed(1)}× margin`}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>

                    {/* ── Pricing: set rarely, so it starts closed ── */}
                    <Card className="credits-card">
                      <button
                        type="button"
                        className="credits-card__toggle"
                        aria-expanded={pricingOpen}
                        onClick={() => setPricingOpen((o) => !o)}
                      >
                        <span>
                          Pricing
                          <span className="credits-card__toggle-note">
                            {isCustom ? 'This client has a plan of its own' : 'This client uses the defaults'}
                          </span>
                        </span>
                        {pricingOpen ? <ChevronDown /> : <ChevronRight />}
                      </button>
                      <Collapse in={pricingOpen}>
                        <div>
                          <Card.Body className="credits-pricing">
                            <p className="credits-hint">
                              A conversation is charged the larger of two numbers: the credits for its tier, or its
                              token cost × the AgentCore multiplier × the tier&apos;s margin ÷ the price of a credit,
                              rounded up. A change applies to conversations metered after you save.
                            </p>
                            <div className="credits-fields">
                              <div>
                                <Form.Label htmlFor="credits-credit-usd">Price of one credit (USD)</Form.Label>
                                <Form.Control
                                  id="credits-credit-usd"
                                  type="number"
                                  step="0.05"
                                  value={config.creditUsd}
                                  onChange={(e) => setGlobal('creditUsd', num(e.target.value))}
                                />
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-agentcore">AgentCore multiplier</Form.Label>
                                <Form.Control
                                  id="credits-agentcore"
                                  type="number"
                                  step="0.001"
                                  value={config.agentcoreMult}
                                  onChange={(e) => setGlobal('agentcoreMult', num(e.target.value))}
                                />
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-margin">Fallback margin</Form.Label>
                                <Form.Control
                                  id="credits-margin"
                                  type="number"
                                  step="0.1"
                                  value={config.margin}
                                  onChange={(e) => setGlobal('margin', num(e.target.value))}
                                />
                                <Form.Text>
                                  Used only when a conversation has no tier. The margins for each tier below are the
                                  real control.
                                </Form.Text>
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-egress">Fixed outgoing IP margin</Form.Label>
                                <Form.Control
                                  id="credits-egress"
                                  type="number"
                                  step="0.01"
                                  min="1"
                                  value={config.fixedEgressMargin}
                                  onChange={(e) => setGlobal('fixedEgressMargin', num(e.target.value))}
                                />
                                <Form.Text>
                                  The AWS cost times this. 1.05 is cost plus 5%. No failover region.
                                </Form.Text>
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-trivial">Trivial-cost cap (USD)</Form.Label>
                                <Form.Control
                                  id="credits-trivial"
                                  type="number"
                                  step="0.01"
                                  value={config.trivialConsumptionUsd}
                                  onChange={(e) => setGlobal('trivialConsumptionUsd', num(e.target.value))}
                                />
                              </div>
                            </div>

                            <h3 className="section-label credits-pricing__label">
                              Lowest margin allowed, for each tier
                            </h3>
                            <p className="credits-hint">
                              A conversation is never charged less than this multiple of what it cost in tokens and
                              AgentCore, whatever the credit prices work out to. Higher tiers keep a fuller margin.
                            </p>
                            <div className="credits-fields credits-fields--four">
                              {TIERS.map((tier) => (
                                <div key={`m-${tier}`}>
                                  <Form.Label htmlFor={`credits-m-${tier}`}>{TIER_LABEL[tier]}</Form.Label>
                                  <Form.Control
                                    id={`credits-m-${tier}`}
                                    type="number"
                                    step="0.05"
                                    value={config.marginsByTier[tier]}
                                    onChange={(e) => setMargin(tier, num(e.target.value))}
                                  />
                                </div>
                              ))}
                            </div>

                            {CONTEXTS.map((ctx) => (
                              <div key={`vt-${ctx}`}>
                                <h3 className="section-label credits-pricing__label">
                                  {ctx === 'chat'
                                    ? 'Credits for a chat, for each tier'
                                    : 'Credits for an agent run, for each tier'}
                                </h3>
                                <div className="credits-fields credits-fields--four">
                                  {TIERS.map((tier) => (
                                    <div key={`vt-${ctx}-${tier}`}>
                                      <Form.Label htmlFor={`credits-vt-${ctx}-${tier}`}>{TIER_LABEL[tier]}</Form.Label>
                                      <Form.Control
                                        id={`credits-vt-${ctx}-${tier}`}
                                        type="number"
                                        step="0.5"
                                        value={config.valueTiers[ctx]?.[tier] ?? 0}
                                        onChange={(e) => setTier(ctx, tier, num(e.target.value))}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}

                            <h3 className="section-label credits-pricing__label">Numa Voice, USD for each minute</h3>
                            <p className="credits-hint">
                              What a phone call costs apart from the AI: the telephone line, the transcript, and Contact
                              Lens when it is on. A call is charged enough credits to cover these rates. The AI summary
                              of the call is charged as normal usage.
                            </p>
                            <div className="credits-fields credits-fields--three">
                              <div>
                                <Form.Label htmlFor="credits-voice-tel">Telephony</Form.Label>
                                <Form.Control
                                  id="credits-voice-tel"
                                  type="number"
                                  step="0.005"
                                  value={config.voiceRates.telephonyPerMin ?? 0}
                                  onChange={(e) => setVoiceRate('telephonyPerMin', num(e.target.value))}
                                />
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-voice-tr">Transcribe</Form.Label>
                                <Form.Control
                                  id="credits-voice-tr"
                                  type="number"
                                  step="0.005"
                                  value={config.voiceRates.transcribePerMin ?? 0}
                                  onChange={(e) => setVoiceRate('transcribePerMin', num(e.target.value))}
                                />
                              </div>
                              <div>
                                <Form.Label htmlFor="credits-voice-cl">Contact Lens</Form.Label>
                                <Form.Control
                                  id="credits-voice-cl"
                                  type="number"
                                  step="0.005"
                                  value={config.voiceRates.contactLensPerMin ?? 0}
                                  onChange={(e) => setVoiceRate('contactLensPerMin', num(e.target.value))}
                                />
                              </div>
                            </div>

                            <p className="credits-hint credits-pricing__synergy">
                              Synergy syncs have no rate to set here. A sync is charged what it cost: the model spend of
                              the run plus its compute, times 1.25. A scan that finds no work is free. More in{' '}
                              <span className="mono">documentation/credits/03-live-metering.md</span>.
                            </p>
                          </Card.Body>
                        </div>
                      </Collapse>
                    </Card>
                  </Col>

                  <Col lg={5}>
                    {/* ── Top-up: applied at once, apart from the save bar ── */}
                    <Card className="credits-card">
                      <Card.Header>Top-up balance</Card.Header>
                      <Card.Body>
                        <p className="credits-topup__balance">
                          {standingReady ? (
                            <>
                              <strong className={`numeric${balance < 0 && !notBilled ? ' credits-topup__owed' : ''}`}>
                                {balance.toLocaleString()}
                              </strong>{' '}
                              credits now, {nzdLabel(balance)}
                              {balance < 0 &&
                                (notBilled ? (
                                  <span>. Past allocation, not billed.</span>
                                ) : (
                                  <span className="credits-topup__owed">. The client owes this.</span>
                                ))}
                            </>
                          ) : standingState === 'loading' ? (
                            'Reading the balance'
                          ) : (
                            'The balance could not be read.'
                          )}
                        </p>
                        <Form.Label htmlFor="credits-topup">Add credits to {client.name}</Form.Label>
                        <InputGroup>
                          <Form.Control
                            id="credits-topup"
                            type="number"
                            step="100"
                            value={topUp}
                            placeholder="1000"
                            onChange={(e) => setTopUp(e.target.value)}
                          />
                          <Button variant="outline-secondary" disabled={saving} onClick={askTopUp}>
                            Add credits
                          </Button>
                        </InputGroup>
                        <Form.Text>
                          Added as soon as you confirm, with no save needed. The balance carries over from month to
                          month and is apart from the monthly credits.
                        </Form.Text>

                        {/* Every change to the balance: top-ups, month-end settlements and adjustments. */}
                        {standing && (
                          <div className="credits-topup__log">
                            <h3 className="section-label">Balance history</h3>
                            {standing.txns.length === 0 ? (
                              <p className="credits-hint mb-0">Nothing has changed this balance yet.</p>
                            ) : (
                              <div className="credits-topup__scroll">
                                <Table size="sm" className="credits-topup__table mb-0">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>What</th>
                                      <th className="text-end">Credits</th>
                                      <th>By</th>
                                      <th>Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {standing.txns.map((t, i) => {
                                      const label =
                                        t.kind === 'topup'
                                          ? 'Top-up'
                                          : t.kind === 'settlement'
                                            ? `Settlement${t.month ? ` (${t.month})` : ''}`
                                            : t.kind === 'adjustment'
                                              ? 'Adjustment'
                                              : t.kind;
                                      return (
                                        <tr key={`${t.createdAt}-${i}`}>
                                          <td className="text-nowrap" title={formatNzDateTime(t.createdAt)}>
                                            {formatNzDate(t.createdAt)}
                                          </td>
                                          <td>{label}</td>
                                          <td className="text-end numeric credits-topup__amount">
                                            {t.credits > 0 ? '+' : ''}
                                            {t.credits.toLocaleString()}
                                          </td>
                                          <td>{t.createdBy ?? ''}</td>
                                          <td>{t.note ?? ''}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>

                    {/* Who may see credits and costs inside the client's workspace. */}
                    <BillingAdminsPanel clientName={client.name} />
                  </Col>
                </Row>
              )}

              {/* ── The one action here that throws work away. It sits last and apart. ── */}
              {!ownAccount && (
                <Card className="credits-reset">
                  <div>
                    <h3 className="credits-reset__title">Reset this client</h3>
                    <p className="credits-hint mb-0">
                      Puts {client.name} back on the default plan and cancels the top-ups on its balance. You see
                      exactly what changes before anything is written.
                    </p>
                  </div>
                  <Button variant="outline-danger" disabled={saving} onClick={() => setPending({ kind: 'reset' })}>
                    Reset to the default plan
                  </Button>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Save bar: shows only when the monthly credits or the pricing changed ── */}
      {client && dirty && !ownAccount && (
        <div className="credits-savebar">
          <span>
            <strong>Unsaved changes</strong> to the monthly credits or pricing for {client.name}.
          </span>
          <div className="credits-savebar__actions">
            <Button variant="outline-secondary" disabled={saving} onClick={handleDiscard}>
              Discard
            </Button>
            <Button variant="primary" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving' : 'Save and send to the client'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Confirm a top-up ── */}
      <Modal show={pending?.kind === 'topup'} onHide={() => setPending(null)} centered>
        {client && pending?.kind === 'topup' && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                Add {pending.credits.toLocaleString()} credits to {client.name}?
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>The credits go on the top-up balance as soon as you confirm. That is {nzdLabel(pending.credits)}.</p>
              <p className="credits-hint mb-0">
                {pending.credits.toLocaleString()} credits × ${config.creditUsd} USD a credit × {fxNzd} NZD to the USD.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleTopUp(pending.credits)}>
                Add credits
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* ── Confirm a reset ── */}
      <Modal show={pending?.kind === 'reset'} onHide={() => setPending(null)} centered>
        {client && pending?.kind === 'reset' && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Reset {client.name} to the default plan?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Confirming does three things:</p>
              <ul className="credits-reset__list">
                <li>Its pricing goes back to the defaults.</li>
                <li>Its monthly credits become {defaultMonthly.toLocaleString()} for every month.</li>
                <li>{resetBalanceLine}</li>
              </ul>
              {standingReady && overflowNow > 0 && (
                <p className="credits-hint">
                  This month&apos;s usage is {overflowNow.toLocaleString()} credits past the monthly credits. That still
                  counts against the balance after the reset.
                </p>
              )}
              {dirty && <p className="credits-hint mb-0">Your unsaved changes on this page are thrown away too.</p>}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button variant="danger" disabled={standingState === 'loading'} onClick={() => void handleReset()}>
                {standingState === 'loading' ? 'Reading the balance' : 'Reset this client'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}
